import { useContext, useEffect, useState } from 'react'
import { UserDetailsContext } from '../../../context/user-details/user-details'
import { Box, Stack, useMediaQuery } from '@mui/material'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { FileUploadHelper } from '../../presentation-components/miscellaneous-components/file-upload-helper'
import { AvatarDisplay } from '../../presentation-components/display-elements-components/avatar-display'
import gaiaTheme from '../../../root-components/theme'
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading'
import { FormColorInputDisplay } from '../../presentation-components/form-field-components/new-and-edit/form-color-input'
import { FormTextInputDisplay } from '../../presentation-components/form-field-components/new-and-edit/form-text-input'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PartnerService from '../../../shared/services/partner/partner.service'
import { IPostPartnerOrganisation } from '../../../shared/services/partner/partner.model'
import { FormImageInputDisplay } from '../../presentation-components/form-field-components/new-and-edit/form-image-input'
import { FormToggleInputDisplay } from '../../presentation-components/form-field-components/new-and-edit/form-toggle-input'

export const PartnerSettingsPage = () => {
  //Servies
  const userDetailsContext = useContext(UserDetailsContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const partnerService = new PartnerService();
  const organisationDetails = userDetailsContext.userDetails?.organisation
  //State
  const [updatedOrganisationDetails, setUpdatedOrganisationDetails] = useState<any>();
  const [editField, setEditField] = useState<string>('');
  const [fileData, setFileData] = useState([]);
  const [iconFileData, setIconFileData] = useState([]);
  const [originalIconFilepath, setOriginalIconFilepath] = useState<string>(organisationDetails?.partnerIconFilepath || '');
  const [tempIconPreview, setTempIconPreview] = useState<string>('');
  const [reversedLogoFileData, setReversedLogoFileData] = useState([]);
  const [tempReversedLogoPreview, setTempReversedLogoPreview] = useState<string>('');
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  //Formik
  const overviewSchema = Yup.object().shape({
    partnerName: Yup.string().required('Company name is required'),
    partnerEmailAddress: Yup.string().email('Invalid email address').nullable(),
    partnerFromEmailAddress: Yup.string().email('Invalid email address').nullable(),
    partnerColor: Yup.string().nullable(),
    customDomainUrl: Yup.string().nullable(),
    hideSignUpButton: Yup.boolean(),
    partnerLoginHeadline: Yup.string().nullable(),
    partnerLoginSubheadline: Yup.string().nullable(),
    partnerLoginEyebrow: Yup.string().nullable(),
    partnerReversedLogoFilepath: Yup.string().nullable(),
  })
  const formik = useFormik<IPostPartnerOrganisation>({
    initialValues: {
      partnerName: '',
      partnerEmailAddress: '',
      partnerFromEmailAddress: '',
      partnerColor: '',
      partnerIconFilepath: '',
      customDomainUrl: '',
      hideSignUpButton: false,
      partnerReversedLogoFilepath: '',
      partnerLoginHeadline: '',
      partnerLoginSubheadline: '',
      partnerLoginEyebrow: '',
    },
    validationSchema: overviewSchema,
    onSubmit: (values) => {
      setEditField('');
      if (values.partnerColor === '') {
        values.partnerColor = null
      }
      if (values.partnerEmailAddress === '') {
        values.partnerEmailAddress = null
      }
      if (values.partnerFromEmailAddress === '') {
        values.partnerFromEmailAddress = null
      }
      if (values.partnerIconFilepath === '') {
        values.partnerIconFilepath = null
      }
      if (values.customDomainUrl === '') {
        values.customDomainUrl = null
      }
      if (values.partnerReversedLogoFilepath === '') {
        values.partnerReversedLogoFilepath = null
      }
      if (values.partnerLoginHeadline === '') {
        values.partnerLoginHeadline = null
      }
      if (values.partnerLoginSubheadline === '') {
        values.partnerLoginSubheadline = null
      }
      if (values.partnerLoginEyebrow === '') {
        values.partnerLoginEyebrow = null
      }
      submitOrganisationHandler(values)
    }
  });

  useEffect(() => {
    setUpdatedOrganisationDetails(organisationDetails)
    setOriginalIconFilepath(organisationDetails?.partnerIconFilepath || '')
    formik.setValues({
      partnerName: organisationDetails?.partnerName,
      partnerEmailAddress: organisationDetails?.partnerEmailAddress,
      partnerFromEmailAddress: organisationDetails?.partnerFromEmailAddress,
      partnerColor: organisationDetails?.partnerColor,
      partnerIconFilepath: organisationDetails?.partnerIconFilepath,
      customDomainUrl: organisationDetails?.customDomainUrl,
      hideSignUpButton: organisationDetails?.hideSignUpButton,
      partnerReversedLogoFilepath: organisationDetails?.partnerReversedLogoFilepath,
      partnerLoginHeadline: organisationDetails?.partnerLoginHeadline,
      partnerLoginSubheadline: organisationDetails?.partnerLoginSubheadline,
      partnerLoginEyebrow: organisationDetails?.partnerLoginEyebrow,
    })
  }, [organisationDetails]);

  useEffect(() => {
    return () => {
      if (tempIconPreview) {
        URL.revokeObjectURL(tempIconPreview);
      }
    };
  }, [tempIconPreview]);

  useEffect(() => {
    return () => {
      if (tempReversedLogoPreview) {
        URL.revokeObjectURL(tempReversedLogoPreview);
      }
    };
  }, [tempReversedLogoPreview]);


  const submitOrganisationHandler = async (postOrganisation: IPostPartnerOrganisation) => {
    try {
      if (iconFileData.length > 0) {
        const form = new FormData();
        form.append("files", iconFileData[0]);
        form.append('organisation', JSON.stringify({ organisationId: userDetailsContext.userDetails?.organisation.id }));
        await partnerService.savePartnerIcon(form);
        setIconFileData([]);
      }
      if (reversedLogoFileData.length > 0) {
        const form = new FormData();
        form.append("files", reversedLogoFileData[0]);
        form.append('organisation', JSON.stringify({ organisationId: userDetailsContext.userDetails?.organisation.id }));
        await partnerService.saveReversedLogo(form);
        setReversedLogoFileData([]);
      }
      await partnerService.editPartner(postOrganisation)
      if (tempIconPreview) {
        URL.revokeObjectURL(tempIconPreview);
        setTempIconPreview('');
      }
      if (tempReversedLogoPreview) {
        URL.revokeObjectURL(tempReversedLogoPreview);
        setTempReversedLogoPreview('');
      }
      userDetailsContext.forceUpdateUserDetails();
      userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
    } catch (err) {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      console.log(err);
    }
  };

  const uploadCompanyLogo = () => {
    const form = new FormData();
    form.append("files", fileData[0]);
    form.append('organisation', JSON.stringify({ organisationId: userDetailsContext.userDetails?.organisation.id }));
    partnerService.savePartnerLogo(form)
      .then(() => {
        setFileData([]);
        userDetailsContext.forceUpdateUserDetails();
      })
  }

  const uploadCompanyIcon = async () => {
    const previewUrl = URL.createObjectURL(iconFileData[0]);
    setTempIconPreview(previewUrl);
    formik.setFieldValue('partnerIconFilepath', previewUrl);
  };

  const uploadReversedLogo = async () => {
    const previewUrl = URL.createObjectURL(reversedLogoFileData[0]);
    setTempReversedLogoPreview(previewUrl);
    formik.setFieldValue('partnerReversedLogoFilepath', previewUrl);
  };


  const handleColorChange = (val: string | React.ChangeEvent<HTMLInputElement>) => {
    if (typeof val === 'string') {
      formik.setFieldValue('partnerColor', val);
    } else {
      formik.setFieldValue('partnerColor', val.target.value);
    }
  };

  const handleHideSignUpToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    formik.setFieldValue('hideSignUpButton', checked);
    submitOrganisationHandler({
      ...formik.values,
      hideSignUpButton: checked,
    });
  };

  const cancelEditHandler = () => {
    setEditField('');
    if (tempIconPreview) {
      URL.revokeObjectURL(tempIconPreview);
      setTempIconPreview('');
    }
    setIconFileData([]);
    if (organisationDetails) {
      formik.setValues({
        partnerName: organisationDetails.partnerName,
        partnerColor: updatedOrganisationDetails.partnerColor,
        partnerIconFilepath: originalIconFilepath,
        partnerEmailAddress: organisationDetails.partnerEmailAddress,
        partnerFromEmailAddress: organisationDetails.partnerFromEmailAddress,
        customDomainUrl: organisationDetails.customDomainUrl,
        hideSignUpButton: organisationDetails.hideSignUpButton,
        partnerReversedLogoFilepath: organisationDetails.partnerReversedLogoFilepath,
        partnerLoginHeadline: organisationDetails.partnerLoginHeadline,
        partnerLoginSubheadline: organisationDetails.partnerLoginSubheadline,
        partnerLoginEyebrow: organisationDetails.partnerLoginEyebrow,
      });
    }
  }

  return (
    <PageOuterContainer>
      {/* {isMobileMode && <Box sx={{ pb: 4 }}>
        <SettingsMobileNavbar />
      </Box>} */}
      {organisationDetails &&
        <PageHeading
          title={organisationDetails.partnerName ?? organisationDetails.name}
          hasTabs={false}
        />}
      <Stack direction={isMobileMode ? 'column' : 'row'} alignItems={isMobileMode ? 'flex-start' : 'center'} sx={{ pb: 4 }}>
        <Box sx={{ mr: 3, mb: isMobileMode ? 2 : 0 }}>
          <AvatarDisplay color={'primary.light'} width={'75px'} height={'75px'} padding={2} fontSize='2rem' logo={true} showPartnerLogo={true} />
        </Box>
        <FileUploadHelper
          disableButton={organisationDetails?.name === null ? true : false}
          fileData={fileData}
          setFileData={setFileData}
          buttonCopy={userDetailsContext.userDetails?.organisation?.logoFilepath ? 'Update partner logo' : 'Upload partner logo'}
          onSubmit={uploadCompanyLogo}
          hidePreview={true} />
      </Stack>
      <Box component='form' onSubmit={formik.handleSubmit}>
        <FormTextInputDisplay
          isTopRow={true}
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'partnerName'}
          title='Name'
          name='partnerName'
          value={formik.values.partnerName || ''}
          touched={formik.touched.partnerName}
          errors={formik.errors.partnerName}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
        />
        <FormTextInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'partnerEmailAddress'}
          title='Contact Email Address'
          name='partnerEmailAddress'
          value={formik.values.partnerEmailAddress || ''}
          touched={formik.touched.partnerEmailAddress}
          errors={formik.errors.partnerEmailAddress}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          tooltip='The email clients will see on the platform to contact your company'
        />
        <FormTextInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'partnerFromEmailAddress'}
          title='Email From Address'
          name='partnerFromEmailAddress'
          value={formik.values.partnerFromEmailAddress || ''}
          touched={formik.touched.partnerFromEmailAddress}
          errors={formik.errors.partnerFromEmailAddress}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          tooltip='The email address used as the sender when we email your clients (requires Postmark sender/domain verification)'
        />
        <FormTextInputDisplay
          isEditPage={true}
          allowEdit={false}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'customDomainUrl'}
          title='Custom Domain URL'
          name='customDomainUrl'
          value={formik.values.customDomainUrl || ''}
          touched={formik.touched.customDomainUrl}
          errors={formik.errors.customDomainUrl}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          tooltip='The custom domain URL (e.g., app.yourcompany.com) that users will use to access the platform. If you need to set or update this domain, please contact the Gaia team.'
        />
        <FormColorInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'partnerColor'}
          title='Color'
          name='partnerColor'
          value={formik.values.partnerColor || ''}
          touched={formik.touched.partnerColor}
          errors={formik.errors.partnerColor}
          onChange={handleColorChange}
          cancelEditHandler={cancelEditHandler}
        />
        <FormImageInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'partnerIconFilepath'}
          title='Icon'
          name='partnerIconFilepath'
          value={formik.values.partnerIconFilepath || organisationDetails?.partnerIconFilepath}
          onChange={() => { }}
          onImageUpload={uploadCompanyIcon}
          cancelEditHandler={cancelEditHandler}
          fileData={iconFileData}
          setFileData={setIconFileData}
          onFileUploadSubmit={uploadCompanyIcon}
          hideSaveButton={true}
          hidePreview={true}
        />
        <FormImageInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'partnerReversedLogoFilepath'}
          title='Login logo (reversed/white)'
          name='partnerReversedLogoFilepath'
          value={formik.values.partnerReversedLogoFilepath || organisationDetails?.partnerReversedLogoFilepath}
          onChange={() => { }}
          onImageUpload={uploadReversedLogo}
          cancelEditHandler={cancelEditHandler}
          fileData={reversedLogoFileData}
          setFileData={setReversedLogoFileData}
          onFileUploadSubmit={uploadReversedLogo}
          hideSaveButton={true}
          hidePreview={true}
        />
        <FormTextInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'partnerLoginHeadline'}
          title='Login headline'
          name='partnerLoginHeadline'
          value={formik.values.partnerLoginHeadline || ''}
          touched={formik.touched.partnerLoginHeadline}
          errors={formik.errors.partnerLoginHeadline}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          tooltip='The large headline shown on your branded login screen. Leave blank to hide it.'
        />
        <FormTextInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'partnerLoginSubheadline'}
          title='Login sub-headline'
          name='partnerLoginSubheadline'
          multiline
          rows={3}
          value={formik.values.partnerLoginSubheadline || ''}
          touched={formik.touched.partnerLoginSubheadline}
          errors={formik.errors.partnerLoginSubheadline}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          tooltip='The supporting sentence shown under the headline on your login screen.'
        />
        <FormTextInputDisplay
          isEditPage={true}
          editField={editField}
          setEditField={setEditField}
          disabled={editField !== 'partnerLoginEyebrow'}
          title='Login eyebrow'
          name='partnerLoginEyebrow'
          value={formik.values.partnerLoginEyebrow || ''}
          touched={formik.touched.partnerLoginEyebrow}
          errors={formik.errors.partnerLoginEyebrow}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          tooltip='The small pill label shown above the headline on your login screen.'
        />
        <FormToggleInputDisplay
          isEditPage={false}
          editField={editField}
          setEditField={setEditField}
          disabled={false}
          isBottomRow={true}
          title='Hide Sign Up Button'
          name='hideSignUpButton'
          value={formik.values.hideSignUpButton || false}
          onChange={handleHideSignUpToggle}
          tooltip='Hide the sign up button on the login page for this partner domain.'
        />
      </Box>
    </PageOuterContainer >
  )
}